import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', label: 'Dashboard' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/tips', label: 'Tips' },
    { to: '/inspect', label: 'New Inspection' },
    { to: '/inspections', label: 'History' },
    { to: '/api', label: 'API' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/20">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-200/50 no-print">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-500/5 to-purple-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 md:gap-3 group">
              <div className="relative w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-brand-600/30 transition-all duration-300 group-hover:shadow-xl group-hover:shadow-brand-600/50 group-hover:scale-105">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <svg
                  className="w-6 h-6 md:w-9 md:h-9 text-white relative z-10 transition-transform duration-300 group-hover:rotate-12"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Main tire body with gradient */}
                  <defs>
                    <radialGradient id="tireGradient" cx="50%" cy="50%">
                      <stop offset="0%" stopColor="currentColor" stopOpacity="0.3"/>
                      <stop offset="100%" stopColor="currentColor" stopOpacity="0.05"/>
                    </radialGradient>
                  </defs>

                  {/* Tire sidewall background */}
                  <circle cx="40" cy="40" r="35" fill="url(#tireGradient)"/>

                  {/* Outer tire edge */}
                  <circle cx="40" cy="40" r="35" stroke="currentColor" strokeWidth="3.5" fill="none" opacity="0.95"/>

                  {/* Middle tire ring */}
                  <circle cx="40" cy="40" r="27" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.4"/>

                  {/* Inner rim */}
                  <circle cx="40" cy="40" r="18" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.9"/>

                  {/* Rim spokes - 5 spokes */}
                  <g stroke="currentColor" strokeWidth="1.5" opacity="0.6" strokeLinecap="round">
                    <line x1="40" y1="22" x2="40" y2="30" />
                    <line x1="51.5" y1="28.5" x2="46.5" y2="34" />
                    <line x1="51.5" y1="51.5" x2="46.5" y2="46" />
                    <line x1="28.5" y1="51.5" x2="33.5" y2="46" />
                    <line x1="28.5" y1="28.5" x2="33.5" y2="34" />
                  </g>

                  {/* Tire tread pattern - more realistic */}
                  <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.95">
                    {/* 12 tread blocks around the tire */}
                    <line x1="40" y1="5" x2="40" y2="12" />
                    <line x1="50.5" y1="8.5" x2="48" y2="15" />
                    <line x1="59.5" y1="16.5" x2="54" y2="21" />
                    <line x1="65.5" y1="27.5" x2="59" y2="31" />
                    <line x1="68" y1="40" x2="61" y2="40" />
                    <line x1="65.5" y1="52.5" x2="59" y2="49" />
                    <line x1="59.5" y1="63.5" x2="54" y2="59" />
                    <line x1="50.5" y1="71.5" x2="48" y2="65" />
                    <line x1="40" y1="75" x2="40" y2="68" />
                    <line x1="29.5" y1="71.5" x2="32" y2="65" />
                    <line x1="20.5" y1="63.5" x2="26" y2="59" />
                    <line x1="14.5" y1="52.5" x2="21" y2="49" />
                    <line x1="12" y1="40" x2="19" y2="40" />
                    <line x1="14.5" y1="27.5" x2="21" y2="31" />
                    <line x1="20.5" y1="16.5" x2="26" y2="21" />
                    <line x1="29.5" y1="8.5" x2="32" y2="15" />
                  </g>

                  {/* AI brain/chip icon in center */}
                  <g opacity="0.95">
                    {/* Circuit board pattern */}
                    <circle cx="40" cy="40" r="8" fill="currentColor" opacity="0.2"/>
                    <path
                      d="M40 32 L36 38 L38.5 38 L35 46 L44 38 L41.5 38 L40 32Z"
                      fill="currentColor"
                      strokeWidth="0.5"
                      stroke="currentColor"
                    />
                  </g>
                </svg>
              </div>

              <div className="hidden sm:flex flex-col -space-y-0.5">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight group-hover:text-gray-700 transition-colors">
                    Tread
                  </span>
                  <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-brand-600 via-brand-700 to-purple-600 bg-clip-text text-transparent">
                    AI
                  </span>
                  <div className="ml-1 md:ml-2 px-1.5 md:px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-md shadow-lg">
                    <span className="text-[8px] md:text-[9px] font-bold text-white tracking-wider">ML</span>
                  </div>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={cn(
                      'relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300',
                      isActive
                        ? 'text-white'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100/80'
                    )}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-brand-700 rounded-xl shadow-lg shadow-brand-600/40" />
                    )}
                    <span className="relative z-10 flex items-center gap-2">
                      {link.to === '/' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      )}
                      {link.to === '/how-it-works' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )}
                      {link.to === '/tips' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )}
                      {link.to === '/inspect' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                      {link.to === '/inspections' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {link.to === '/api' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      )}
                      {link.label}
                    </span>
                  </Link>
                );
              })}

              {/* App Store Link */}
              <a
                href="https://apps.apple.com/us/app/tread-ai/id6755405651"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 px-4 py-2 bg-black text-white rounded-xl text-sm font-semibold flex items-center gap-2 hover:bg-gray-800 transition-colors shadow-lg"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                App Store
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Slide-out */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-lg">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'block px-4 py-3 rounded-xl text-sm font-semibold transition-all',
                      isActive
                        ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    <span className="flex items-center gap-3">
                      {link.to === '/' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      )}
                      {link.to === '/how-it-works' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )}
                      {link.to === '/tips' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )}
                      {link.to === '/inspect' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                      {link.to === '/inspections' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {link.to === '/api' && (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      )}
                      {link.label}
                    </span>
                  </Link>
                );
              })}

              {/* App Store Link - Mobile */}
              <a
                href="https://apps.apple.com/us/app/tread-ai/id6755405651"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 rounded-xl text-sm font-semibold bg-black text-white mt-2"
              >
                <span className="flex items-center gap-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Download on App Store
                </span>
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {children}
      </main>

      <footer className="relative bg-white/80 backdrop-blur-xl border-t border-gray-200/50 mt-20 no-print">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-500/5 to-purple-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl flex items-center justify-center shadow-lg shadow-brand-600/30">
                <svg className="w-6 h-6 text-white" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="30" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.95"/>
                  <circle cx="40" cy="40" r="15" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.7"/>
                  <line x1="40" y1="10" x2="40" y2="16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
                  <line x1="40" y1="64" x2="40" y2="70" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
                  <line x1="10" y1="40" x2="16" y2="40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
                  <line x1="64" y1="40" x2="70" y2="40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
                </svg>
              </div>
              <div>
                <div className="font-bold text-gray-900">TreadAI</div>
                <div className="text-xs text-gray-500">AI-Powered Tire Inspection</div>
              </div>
            </div>

            <div className="text-center md:text-right">
              <p className="text-sm text-gray-600 font-medium">
                Professional tire safety analysis with machine learning
              </p>
              <div className="flex items-center justify-center md:justify-end gap-4 mt-3">
                <a
                  href="https://apps.apple.com/us/app/tread-ai/id6755405651"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  App Store
                </a>
                <Link
                  to="/terms"
                  className="text-xs text-gray-500 hover:text-brand-600 transition-colors"
                >
                  Terms of Service
                </Link>
                <a
                  href="https://treadai.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-brand-600 transition-colors"
                >
                  Privacy Policy
                </a>
                <a
                  href="https://www.linkedin.com/company/tread-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#0077b5] transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-3 max-w-md mx-auto md:mx-0">
                <span className="font-semibold text-amber-600">Disclaimer:</span> TreadAI is for informational purposes only. Not a substitute for professional tire inspection. Use at your own risk.
              </p>
              <span className="text-xs text-gray-400 mt-2 block">
                © 2025 TreadAI. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
